/* =========================================================================
   OP STICKER animations  (ported from Shape Theme "shapes-animations.js")
   -------------------------------------------------------------------------
   Fully self-contained and namespaced under window.OPSticker. It carries its
   own debounce and its own parallax-intensity default, so it depends on NO
   OrganicPeach globals and cannot collide with existing theme scripts.

   Handles:
     - Parallax stickers  [data-op-parallax-container] / [data-op-parallax-element]
     - "Rotate while scrolling" stickers  (.op-sticker--rotate-when-scrolling)
     - A "portal" that relocates each section's sticker overlay to a single
       top-level layer on <body>. This is what guarantees a sticker is never
       clipped by an ancestor's `overflow: hidden`, and never visually
       covered by a later, unrelated section — both of which can happen with
       ordinary nested CSS stacking/overflow no matter which section wrapper
       a sticker lives in. See OPStickerPortal below.

   Respects prefers-reduced-motion. Re-initialises on theme-editor events.
   ========================================================================= */
(function () {
  "use strict";

  if (window.OPSticker && window.OPSticker.initialized) return;

  var reduceMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this,
        args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  /* -----------------------------------------------------------------------
     OPStickerPortal
     -------------------------------------------------------------------------
     Moves each section's `.op-sticker-layer` (the whole overlay, with all of
     that section's stickers inside it) out of the section's own DOM subtree
     and into a single shared layer appended directly to <body>. The moved
     layer is repositioned with plain document (page) coordinates computed
     from its original host's bounding box, so visually nothing changes —
     it still sits exactly over its own section — but it is no longer a
     descendant of that section, so no ancestor `overflow: hidden` and no
     ancestor stacking context (e.g. an `isolate`/z-index wrapper) can clip
     it or bury it under a later section.

     If JavaScript is unavailable, the sticker simply renders in its
     original in-place position (current behaviour) — a safe, non-breaking
     fallback for a purely decorative, optional enhancement.
     ----------------------------------------------------------------------- */
  function OPStickerPortal() {
    this.portal = null;
    this.mounts = []; // [{ host, mount, ro }]
  }

  OPStickerPortal.prototype.ensurePortal = function () {
    if (this.portal && document.body.contains(this.portal)) return this.portal;
    var portal = document.getElementById("op-sticker-portal");
    if (!portal) {
      portal = document.createElement("div");
      portal.id = "op-sticker-portal";
      portal.setAttribute("aria-hidden", "true");
      document.body.appendChild(portal);
    }
    this.portal = portal;
    return portal;
  };

  OPStickerPortal.prototype.updateMountRect = function (host, mount) {
    var rect = host.getBoundingClientRect();
    var scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    var scrollX = window.pageXOffset || document.documentElement.scrollLeft || 0;
    mount.style.top = Math.round(rect.top + scrollY) + "px";
    mount.style.left = Math.round(rect.left + scrollX) + "px";
    mount.style.width = Math.round(rect.width) + "px";
    mount.style.height = Math.round(rect.height) + "px";
  };

  OPStickerPortal.prototype.mount = function (layer) {
    if (layer.getAttribute("data-op-portal-mounted") === "true") return;
    var host = layer.parentElement;
    if (!host) return;
    var portal = this.ensurePortal();

    var mount = document.createElement("div");
    mount.className = "op-sticker-portal-mount";
    portal.appendChild(mount);
    mount.appendChild(layer); // moves the node (and all its stickers) as-is
    layer.setAttribute("data-op-portal-mounted", "true");

    this.updateMountRect(host, mount);

    var self = this;
    var ro = null;
    if ("ResizeObserver" in window) {
      ro = new ResizeObserver(function () {
        self.updateMountRect(host, mount);
      });
      ro.observe(host);
    }
    this.mounts.push({ host: host, mount: mount, ro: ro });
  };

  OPStickerPortal.prototype.mountAll = function () {
    var layers = document.querySelectorAll(
      '.op-sticker-layer:not([data-op-portal-mounted="true"])'
    );
    for (var i = 0; i < layers.length; i++) this.mount(layers[i]);
  };

  OPStickerPortal.prototype.refreshAll = function () {
    for (var i = this.mounts.length - 1; i >= 0; i--) {
      var entry = this.mounts[i];
      if (document.body.contains(entry.host)) {
        this.updateMountRect(entry.host, entry.mount);
      } else {
        // Original section was removed (e.g. deleted in the theme editor).
        if (entry.ro) entry.ro.disconnect();
        if (entry.mount && entry.mount.parentNode) {
          entry.mount.parentNode.removeChild(entry.mount);
        }
        this.mounts.splice(i, 1);
      }
    }
  };

  function OPStickerAnimations() {
    this.parallaxItems = [];
    this.rotateItems = [];
    this.observer = null;
    this.rafPending = false;
    this.position = 0;
    this.intensity = 0.3; // sensible default (Shape maps ~30/100)
  }

  OPStickerAnimations.prototype.inViewport = function (el) {
    if (!el || el.nodeType !== 1) return false;
    var html = document.documentElement;
    var rect = el.getBoundingClientRect();
    return (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.bottom >= 0 &&
      rect.right >= 0 &&
      rect.left <= html.clientWidth &&
      rect.top <= html.clientHeight
    );
  };

  OPStickerAnimations.prototype.cache = function () {
    var containers = document.querySelectorAll(
      "[data-op-parallax-container]"
    );
    this.parallaxItems = [];
    for (var i = 0; i < containers.length; i++) {
      var el = containers[i];
      var element = el.querySelector("[data-op-parallax-element]");
      if (!element) continue;
      var speed = parseInt(el.getAttribute("data-op-parallax-speed"), 10);
      if (isNaN(speed)) speed = 3;
      this.parallaxItems.push({
        id: el.getAttribute("data-op-parallax-id"),
        container: el,
        element: element,
        height:
          element.clientHeight ||
          element.offsetHeight ||
          element.scrollHeight ||
          0,
        speed: speed * -1,
        visible: this.inViewport(el),
      });
    }
    this.rotateItems = document.querySelectorAll(
      ".op-sticker--rotate-when-scrolling"
    );
  };

  OPStickerAnimations.prototype.observe = function () {
    if (this.observer) this.observer.disconnect();
    var self = this;
    this.observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var id = entry.target.getAttribute("data-op-parallax-id");
          for (var i = 0; i < self.parallaxItems.length; i++) {
            if (self.parallaxItems[i].id === id) {
              self.parallaxItems[i].visible = entry.isIntersecting;
            }
          }
        });
      },
      { rootMargin: "0px 0px 20% 0px", threshold: 0 }
    );
    for (var i = 0; i < this.parallaxItems.length; i++) {
      this.observer.observe(this.parallaxItems[i].container);
    }
  };

  OPStickerAnimations.prototype.animate = function () {
    var screenHeight = window.innerHeight;
    for (var i = 0; i < this.parallaxItems.length; i++) {
      var item = this.parallaxItems[i];
      if (!item.visible) continue;
      var top = item.container.getBoundingClientRect().top;
      var pct =
        (screenHeight - top) / (screenHeight + item.height) - 0.5;
      var value =
        Math.round(
          this.intensity * (item.speed * (pct * 100)) * 100 +
            Number.EPSILON
        ) / 100;
      item.element.style.transform = "translateY(" + value + "px)";
    }
    var rot = window.pageYOffset / 3;
    for (var r = 0; r < this.rotateItems.length; r++) {
      this.rotateItems[r].style.transform = "rotate(" + rot + "deg)";
    }
    this.rafPending = false;
  };

  OPStickerAnimations.prototype.onScroll = function () {
    var pos =
      (document.documentElement || document.body.parentNode || document.body)
        .scrollTop || window.pageYOffset;
    if (pos === this.position) return;
    this.position = pos;
    if (this.rafPending) return;
    this.rafPending = true;
    var self = this;
    requestAnimationFrame(function () {
      self.animate();
    });
  };

  OPStickerAnimations.prototype.init = function () {
    // The portal runs regardless of prefers-reduced-motion — it only
    // relocates static markup, it does not animate anything.
    portalInstance.mountAll();
    portalInstance.refreshAll();

    if (!this._portalBound) {
      this._portalBound = true;
      window.addEventListener(
        "resize",
        debounce(function () {
          portalInstance.refreshAll();
        }, 250)
      );
      window.addEventListener("load", function () {
        portalInstance.refreshAll();
      });
      document.addEventListener(
        "shopify:section:load",
        debounce(function () {
          portalInstance.mountAll();
          portalInstance.refreshAll();
        }, 200)
      );
      document.addEventListener(
        "shopify:section:unload",
        debounce(function () {
          portalInstance.refreshAll();
        }, 200)
      );
    }

    if (reduceMotion) return;
    // Read an optional intensity hint from any layer on the page.
    var host = document.querySelector("[data-op-parallax-intensity]");
    if (host) {
      var raw = parseInt(host.getAttribute("data-op-parallax-intensity"), 10);
      if (!isNaN(raw)) this.intensity = Math.max(0.01, Math.min(1.1, raw / 100));
    }
    this.cache();
    this.observe();
    this.animate();

    if (!this._bound) {
      this._bound = true;
      var self = this;
      document.addEventListener(
        "scroll",
        function () {
          self.onScroll();
        },
        { passive: true }
      );
      window.addEventListener(
        "resize",
        debounce(function () {
          self.init();
        }, 250)
      );
      document.addEventListener(
        "shopify:section:load",
        debounce(function () {
          self.init();
        }, 200)
      );
      document.addEventListener(
        "shopify:block:select",
        debounce(function () {
          self.init();
        }, 200)
      );
    }
  };

  var portalInstance = new OPStickerPortal();
  var instance = new OPStickerAnimations();
  window.OPSticker = { initialized: true, instance: instance, portal: portalInstance };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      instance.init();
    });
  } else {
    instance.init();
  }
})();
